import { Box, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import * as chartUtils from "chartUtils";
import * as dates from "common/dates";
import DefaultLoader from "components/common/DefaultLoader";
import * as support from "components/inputs/claims/AggregatedClaimsTable.support";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { useClaimsAggregateQuery } from "services/claimsService";
import * as featureFlagHooks from "store/hooks/featureFlagHooks";
import useDebouncedValue from "store/hooks/useDebouncedValue";
import * as claimsSelectors from "store/selectors/input/claims/claimsSelectors";
import * as exposureSelectors from "store/selectors/input/exposure/exposureSelectors";
import * as programSelectors from "store/selectors/input/program/programSelectors";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";

const normalizeAggregates = ({
  usingAggregatedClaimsInput,
  manualData,
  lossCategories,
  apiData,
  inception,
  hasClaimsQueryKey,
}) => {
  if (usingAggregatedClaimsInput) {
    return manualData.map((item) => ({
      label: support.coverageLabel(lossCategories, item.coverage),
      data: item.data,
    }));
  }

  if (!hasClaimsQueryKey) {
    return [];
  }

  return support.fillMissingYears(apiData, lossCategories, {
    endDate: inception,
    numberOfYears: 10,
  });
};

const LossesByCoverageChart = () => {
  const theme = useTheme();
  const PIE_COLORS = [
    theme.brand.chart.primary,
    "#2E9996",
    "#2E4E99",
    "#682E99",
    "#992E4F",
  ];
  const claimsQuery = useDebouncedValue(
    useSelector(pricingSelectors.selectPricingClaims)
  );
  const inception = useSelector(programSelectors.getInception);
  const lossCategoriesRaw = useSelector(exposureSelectors.selectLossCategories);
  const aggregatedClaims = useSelector(claimsSelectors.selectAggregatedClaims);
  const usingAggregatedClaimsInput = aggregatedClaims?.manualMode ?? false;
  const manualDataRaw = aggregatedClaims?.data;
  const hasClaimsQueryKey = claimsQuery?.keys?.length > 0;
  const { isOn: bucketByPolicyYear } = featureFlagHooks.useFeatureFlag(
    "claims/bucketAggregatedClaimsByPolicyYear",
    {
      default: true,
    }
  );
  const start = bucketByPolicyYear
    ? dates.adjustDate(inception, { years: -10 })
    : null;

  const { data, isLoading } = useClaimsAggregateQuery(
    {
      claimsQuery: {
        ...claimsQuery,
        start,
      },
    },
    { skip: usingAggregatedClaimsInput || !hasClaimsQueryKey }
  );

  const lossCategories = useMemo(() => lossCategoriesRaw ?? [], [
    lossCategoriesRaw,
  ]);
  const manualData = useMemo(() => manualDataRaw ?? [], [manualDataRaw]);

  const aggregates = useMemo(
    () =>
      normalizeAggregates({
        usingAggregatedClaimsInput,
        manualData,
        lossCategories,
        apiData: data,
        inception,
        hasClaimsQueryKey,
      }),
    [
      usingAggregatedClaimsInput,
      manualData,
      lossCategories,
      data,
      inception,
      hasClaimsQueryKey,
    ]
  );

  const chartData = useMemo(
    () =>
      aggregates
        .map((coverage) => ({
          name: coverage.label,
          value: (coverage.data ?? []).reduce(
            (total, row) => total + (row.incurred ?? 0),
            0
          ),
        }))
        .filter((item) => item.value > 0),
    [aggregates]
  );

  const chartHeight = 180;

  return (
    <div className={"print-section"}>
      <span className={"print-label"}>{"Losses by Coverage"}</span>
      {isLoading ? (
        <Stack alignItems={"center"} paddingY={4}>
          <DefaultLoader size={32} />
        </Stack>
      ) : chartData.length === 0 ? (
        <Typography variant={"caption"} color={"text.secondary"}>
          {"No loss data available"}
        </Typography>
      ) : (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Stack spacing={0.75} sx={{ minWidth: "180px" }}>
            {chartData.map((entry, index) => (
              <Box
                key={`losses-by-coverage-legend-${entry.name}-${index}`}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  fontSize: "11px",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: PIE_COLORS[index % PIE_COLORS.length],
                    }}
                  />
                  <span>{entry.name}</span>
                </Box>
                <span>{chartUtils.tickFormatter(entry.value)}</span>
              </Box>
            ))}
          </Stack>
          <div
            style={{
              position: "relative",
              width: "100%",
              height: chartHeight,
              maxWidth: "280px",
            }}
          >
            <ResponsiveContainer width={"100%"} height={"100%"}>
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey={"value"}
                  nameKey={"name"}
                  label={false}
                  legendType={"circle"}
                  isAnimationActive={false}
                  innerRadius={42}
                  outerRadius={70}
                >
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`losses-by-coverage-${entry.name}-${index}`}
                      fill={PIE_COLORS[index % PIE_COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={chartUtils.formatToolTip}
                  contentStyle={{
                    fontSize: "11px",
                    padding: "6px 8px",
                    borderRadius: "6px",
                  }}
                  itemStyle={{ fontSize: "11px", padding: "2px 0" }}
                  labelStyle={{ fontSize: "11px", marginBottom: "2px" }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Box>
      )}
    </div>
  );
};

export default LossesByCoverageChart;
